import PasswordField from "@/src/components/auth/password-field.rhf";
import PasswordStrengthValidator from "@/src/components/auth/password-strength-validator";
import ScButton from "@/src/components/ui-kit/button";
import { useResetPassword } from "@/src/hooks/features/auth/use-reset-password";
import { useTheme } from "@/src/styles/theme-provider";
import { useRouter } from "expo-router";
import { useRef } from "react";
import {
  ActivityIndicator,
  Image,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from "react-native";

type PasswordScreenType = Readonly<"reset-password" | "set-password">;

type ResetPasswordProps = Readonly<{
  type?: PasswordScreenType;
}>;

export default function ResetPassword({
  type = "reset-password",
}: ResetPasswordProps) {
  const { varStyle } = useTheme();
  const router = useRouter();
  const {
    control,
    handleSubmit,
    errors,
    password,
    formError,
    mounted,
    isSubmitting,
    activated,
    activateAccount,
    activating,
    onSubmit,
    t,
  } = useResetPassword(type);

  const confirmPasswordRef = useRef<TextInput>(null);

  if (!mounted) {
    return null;
  }

  const screenConfig = {
    "reset-password": {
      title: t("LABEL_RESET_PASSWORD"),
      hint: t("HINT_RESET_PASSWORD"),
      buttonText: isSubmitting ? "BTN_SUBMITTING" : "BTN_RESET_PASSWORD",
    },
    "set-password": {
      title: t("LABEL_SET_NEW_PASSWORD"),
      hint: t("HINT_SET_NEW_PASSWORD"),
      buttonText: isSubmitting ? "BTN_SUBMITTING" : "BTN_SET_PASSWORD",
    },
  }[type];

  return (
    <View style={varStyle} className="flex-1 bg-chalk web:flex-row">
      <View className="hidden xl:flex flex-1 items-end justify-center p-8">
        <Image
          source={require("../../../assets/images/Forgot_password.png")}
          className="w-full h-full max-w-xl"
          resizeMode="contain"
        />
      </View>

      <View className="flex-1 items-center justify-center web:px-6 py-4">
        <ScrollView
          className="w-full"
          contentContainerClassName="items-start justify-center min-h-full"
          showsVerticalScrollIndicator={false}
        >
          <View className="w-full max-w-lg px-4 py-0">
            <View className="rounded-xl border border-ash-3 bg-chalk p-6 web:p-5">
              <View className="flex-row justify-center items-center mt-6 mb-4 web:mt-6 web:mb-3">
                <Image
                  source={require("../../../assets/images/club_logo.png")}
                  style={{ width: 28, height: 28 }}
                  resizeMode="contain"
                />
                <Text className="ml-2 text-primary text-2xl font-bold">
                  {t("LABEL_SELISE_LOGO")}
                </Text>
              </View>

              {type === "set-password" ? (
                <SetPasswordStatus
                  activated={activated}
                  activating={activating}
                  formError={formError}
                  onActivate={activateAccount}
                  t={t}
                  onBackToLogin={() => router.replace("/login")}
                />
              ) : (
                <>
                  <Text className="web:text-xl text-2xl font-bold text-center mb-2 web:mb-1 text-onyx">
                    {screenConfig.title}
                  </Text>
                  <Text className="web:text-xs text-sm text-center text-slate mb-6 web:mb-4">
                    {screenConfig.hint}
                  </Text>

                  <PasswordField
                    control={control}
                    name="password"
                    label={t("LABEL_NEW_PASSWORD")}
                    placeholder={t("HINT_NEW_PASSWORD")}
                    error={errors.password?.message}
                    returnKeyType="next"
                    blurOnSubmit={false}
                    onSubmitEditing={() => confirmPasswordRef.current?.focus()}
                  />

                  <PasswordField
                    ref={confirmPasswordRef}
                    control={control}
                    name="confirmPassword"
                    label={t("LABEL_CONFIRM_PASSWORD")}
                    placeholder={t("HINT_CONFIRM_PASSWORD")}
                    error={errors.confirmPassword?.message}
                    returnKeyType="go"
                    onSubmitEditing={handleSubmit(onSubmit)}
                  />

                  <PasswordStrengthValidator password={password} />

                  {formError && (
                    <Text className="text-crimson text-sm text-center mb-4">
                      {formError}
                    </Text>
                  )}

                  <ScButton
                    text={screenConfig.buttonText}
                    onPress={handleSubmit(onSubmit)}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    className="mb-6 h-16 web:h-10 web:mb-4 rounded-lg"
                  />
                </>
              )}
            </View>

            <View className="mt-12 items-center mb-6 web:mt-4 web:mb-2">
              <Image
                source={require("../../../assets/images/selise_logo.png")}
                style={{ width: 100, height: 36 }}
                resizeMode="contain"
              />
            </View>
          </View>
        </ScrollView>
      </View>
    </View>
  );
}

/**
 * No password form — invited accounts already have one (see
 * use-reset-password.ts) — just the states this code redemption can be in.
 *
 * Redemption waits for a real click (`onActivate`) rather than firing on
 * mount: a bare page load is exactly what corporate mail link-scanners
 * (Outlook Safe Links, Defender for Office 365) pre-fetch before the
 * recipient ever opens the email, which silently burns the single-use code.
 * A non-interactive scanner fetches the URL but does not press a button.
 */
function SetPasswordStatus({
  activated,
  activating,
  formError,
  onActivate,
  t,
  onBackToLogin,
}: Readonly<{
  activated: boolean;
  activating: boolean;
  formError: string | undefined;
  onActivate: () => void;
  t: (key: string) => string;
  onBackToLogin: () => void;
}>) {
  if (formError) {
    // Blocks reports this as a plain field error ("The code has expired…"),
    // same shape as every other activation failure — call out the expired
    // case specifically since the fix for it differs (ask for a new invite,
    // not "try again").
    const isExpired = /expired/i.test(formError);

    return (
      <View className="items-center gap-2 py-6">
        <Text className="web:text-xl text-2xl font-bold text-center text-onyx">
          {isExpired ? t("LABEL_LINK_EXPIRED") : t("LABEL_ACTIVATION_FAILED")}
        </Text>
        <Text className="web:text-xs text-sm text-center text-slate mb-2">
          {isExpired ? t("HINT_ACTIVATION_LINK_EXPIRED") : formError}
        </Text>
        <Pressable onPress={onBackToLogin}>
          <Text className="text-primary underline web:text-xs text-sm font-semibold">
            {t("BTN_BACK_TO_SIGN_IN")}
          </Text>
        </Pressable>
      </View>
    );
  }

  if (activated) {
    return (
      <View className="items-center gap-2 py-6">
        <Text className="web:text-xl text-2xl font-bold text-center text-onyx">
          {t("SUCCESS_ACCOUNT_ACTIVATED")}
        </Text>
        <Text className="web:text-xs text-sm text-center text-slate">
          {t("HINT_ACCOUNT_ACTIVATED")}
        </Text>
      </View>
    );
  }

  if (activating) {
    return (
      <View className="items-center gap-3 py-6">
        <ActivityIndicator size="large" />
        <Text className="web:text-xs text-sm text-center text-slate">
          {t("LABEL_ACTIVATING_ACCOUNT")}
        </Text>
      </View>
    );
  }

  return (
    <View className="items-center gap-3 py-6">
      <Text className="web:text-xl text-2xl font-bold text-center text-onyx">
        {t("LABEL_ACTIVATE_ACCOUNT")}
      </Text>
      <Text className="web:text-xs text-sm text-center text-slate mb-2">
        {t("HINT_ACTIVATE_ACCOUNT")}
      </Text>
      <ScButton text="BTN_ACTIVATE_ACCOUNT" onPress={onActivate} />
    </View>
  );
}
